/** Statusfilter der Rechnungsübersicht. */

/** Sonderwert im Statusfilter: keine Einschränkung. */
export const INVOICE_STATE_FILTER_ALL = 0;

/** Sonderwert im Statusfilter: nur Rechnungen mit nicht abschließendem Status. */
export const INVOICE_STATE_FILTER_OPEN = -1;

interface IStateLike {
  idInvoiceState?: number;
  /** Das Backend liefert dieses TINYINT-Feld als `0`/`1`, nicht als Boolean —
   *  das zod-Schema in IInvoiceState.ts behauptet `boolean`, wird aber nie zum
   *  Parsen benutzt, sondern nur für den Typ. Deshalb hier beide Formen. */
  isCloseing?: boolean | number;
}

/** Schließt dieser Status die Rechnung ab (gezahlt, storniert)?
 *
 *  Bewusst über Truthiness statt über einen Vergleich mit `false`: ein
 *  `isCloseing === false` traf auf die tatsächlich gelieferte `0` nie zu und
 *  hat den Filter „Nur Offene" komplett leer laufen lassen (QS #8).
 *
 *  Derselbe Fehlertyp ist in `Admin/RatingForm/ratingFormUtils.ts`
 *  (`flagFromBackend`) schon einmal beschrieben worden — TINYINT-Flags aus dem
 *  Backend nie strikt gegen `true`/`false` vergleichen.
 */
export const isClosingInvoiceState = (state?: IStateLike | null): boolean =>
  Boolean(state?.isCloseing);

/** Passt die Rechnung zum gewählten Statusfilter?
 *
 *  Ein Status, der in der Stammdatenliste nicht vorkommt, gilt als offen — eine
 *  Rechnung stillschweigend zu verschlucken ist der schlechtere Fehler.
 */
export const matchesInvoiceStateFilter = (
  invoice: { idInvoiceState?: number },
  states: IStateLike[] | undefined,
  idStateFilter: number
): boolean => {
  if (idStateFilter === INVOICE_STATE_FILTER_ALL) return true;

  if (idStateFilter === INVOICE_STATE_FILTER_OPEN) {
    if (!states) return true;
    const state = states.find(
      candidate => candidate.idInvoiceState === invoice.idInvoiceState
    );
    return !isClosingInvoiceState(state);
  }

  return invoice.idInvoiceState === idStateFilter;
};

/** Namen der Status, die „Nicht abgeschlossen" umfasst — für die Erklärung im
 *  Filter. Aus den Stammdaten gelesen, nicht festverdrahtet: kommt ein Status
 *  dazu, steht er automatisch im Hinweis. */
export const openInvoiceStateNames = (
  states: IStateLike[] | undefined
): string[] =>
  (states ?? [])
    .filter(state => !isClosingInvoiceState(state))
    .map(state => (state as { InvoiceState?: string }).InvoiceState ?? '')
    .filter(name => name.trim() !== '');

/** Erklärungstext für den Statusfilter. Beantwortet die Frage, die sich beim
 *  Lesen des Dropdowns stellt: „Nicht abgeschlossen" und „Offen" stehen
 *  untereinander und bedeuten Verschiedenes. */
export const invoiceStateFilterHint = (
  states: IStateLike[] | undefined
): string => {
  const open = openInvoiceStateNames(states);
  const covered =
    open.length > 0 ? open.join(', ') : 'alle nicht abschließenden Status';

  return (
    `„Nicht abgeschlossen" fasst alle Status zusammen, die eine Rechnung ` +
    `offen lassen: ${covered}. Die einzelnen Einträge darunter filtern ` +
    `jeweils auf genau einen Status — „Offen" also nur auf diesen einen.`
  );
};
